import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// ====================== Styled Components ======================

const CalculatorCard = styled(Paper)({
  maxWidth: "950px",
  margin: "0 auto",
  borderRadius: "15px",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  border: "1px solid #e5e7eb",
  overflow: "hidden",
  backgroundColor: "#fff",
});

const WizardNav = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  padding: "1rem 2rem",
  background: "#f9fafb",
  borderBottom: "1px solid #e5e7eb",
});

const NavStep = styled(Box)(({ active }) => ({
  color: active ? "#313232" : "#6b7280",
  fontWeight: active ? 600 : 500,
  fontSize: "0.9rem",
  padding: "0.5rem 1rem",
  borderRadius: "6px",
  backgroundColor: active ? "#eeeeee" : "transparent",
}));

const LabelRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "0.75rem",
});

const RangeValue = styled(Typography)({
  fontWeight: 700,
  color: "#51842f",
  fontSize: "0.95rem",
});

const InfoTip = styled(Box)({
  fontSize: "0.8rem",
  color: "#6b7280",
  marginTop: "0.75rem",
  background: "#f3f4f6",
  padding: "0.5rem 0.75rem",
  borderRadius: "4px",
  display: "flex",
  alignItems: "flex-start",
  gap: "6px",
  "&::before": {
    content: "'ⓘ'",
    color: "#3b82f6",
    flexShrink: 0,
  },
});

const RadioCard = styled(Box)(({ checked }) => ({
  cursor: "pointer",
  marginBottom: "0.75rem",
  "& .card-content": {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.75rem 1rem",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    background: "#fff",
    borderColor: checked ? "#3b82f6" : "#d1d5db",
  },
  "& .radio-circle": {
    width: "18px",
    height: "18px",
    border: "2px solid #d1d5db",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    borderColor: checked ? "#3b82f6" : "#d1d5db",
    "&::after": {
      content: "''",
      width: "10px",
      height: "10px",
      background: "#3b82f6",
      borderRadius: "50%",
      transform: checked ? "scale(1)" : "scale(0)",
      transition: "transform 0.2s",
    },
  },
}));

const SegmentControl = styled(Box)({
  display: "flex",
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  overflow: "hidden",
});

const SegmentOption = styled(Box)(({ selected }) => ({
  flex: 1,
  textAlign: "center",
  padding: "0.6rem",
  cursor: "pointer",
  background: selected ? "#dedede" : "#fff",
  color: selected ? "#353232" : "#4b5563",
  fontWeight: 500,
  fontSize: "0.9rem",
}));

const ToggleSwitch = styled(Box)({
  display: "inline-flex",
  background: "#f3f4f6",
  borderRadius: "6px",
  padding: "2px",
});

const ToggleOption = styled(Box)(({ selected }) => ({
  padding: "0.4rem 1rem",
  borderRadius: "4px",
  cursor: "pointer",
  background: selected ? "#fff" : "transparent",
  color: selected ? "#51842f" : "#4b5563",
  fontWeight: selected ? 600 : 500,
  boxShadow: selected ? "0 1px 2px rgba(0, 0, 0, 0.1)" : "none",
}));

const DocCard = styled(Box)(({ checked }) => ({
  display: "flex",
  gap: "1rem",
  alignItems: "flex-start",
  padding: "1rem",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: "#fff",
  borderColor: checked ? "#51842f" : "#e5e7eb",
  "&:hover": { borderColor: "#9ca3af" },
  "& .doc-box": {
    width: "20px",
    height: "20px",
    border: "2px solid #d1d5db",
    borderRadius: "4px",
    position: "relative",
    flexShrink: 0,
    marginTop: "2px",
    backgroundColor: checked ? "#51842f" : "#fff",
    borderColor: checked ? "#51842f" : "#d1d5db",
    "&::after": checked
      ? {
          content: "''",
          position: "absolute",
          left: "6px",
          top: "2px",
          width: "4px",
          height: "10px",
          border: "solid white",
          borderWidth: "0 2px 2px 0",
          transform: "rotate(45deg)",
        }
      : {},
  },
}));

const ResultHeader = styled(Box)({
  background: "#8bc34a1f",
  color: "#272727",
  padding: "1rem",
  borderRadius: "15px",
  border: "1px solid #cccccc78",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "15px",
});

const ScoreCardMain = styled(Paper)({
  background: "#fff",
  borderRadius: "15px",
  padding: "2rem",
  textAlign: "center",
  border: "1px solid #cccccc87",
  marginTop: "38px",
});

const BdSection = styled(Box)({
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  background: "#fff",
  marginBottom: "1rem",
  overflow: "hidden",
});

const BdHeader = styled(Box)({
  background: "#f9fafb",
  padding: "0.75rem 1rem",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontWeight: 600,
  fontSize: "0.85rem",
  color: "#6b7280",
});

const BdBadge = styled(Box)({
  background: "#fff",
  border: "1px solid #e5e7eb",
  padding: "2px 8px",
  borderRadius: "12px",
  fontSize: "0.75rem",
});

const BdItem = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.75rem 1rem",
  borderBottom: "1px solid #f3f4f6",
  "&:last-child": { borderBottom: "none" },
});

const BdLabel = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const BdPoints = styled(Box)({
  textAlign: "right",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  "& .plus": { color: "#10b981", fontWeight: 700, fontSize: "0.9rem" },
  "& small": { fontSize: "0.7rem", color: "#9ca3af" },
});

const FooterActions = styled(Box)({
  padding: "1.5rem 2rem",
  background: "#fff",
  borderTop: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
});

const PrimaryButton = styled(Button)({
  backgroundColor: "#51842f",
  color: "#fff",
  padding: "0.75rem 1.5rem",
  fontWeight: 600,
  borderRadius: "6px",
  "&:hover": { backgroundColor: "#406827" },
});

// ====================== Main Component ======================

const AgriTradeCreditAssess = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [showResult, setShowResult] = useState(false);

  // Form state
  const [region, setRegion] = useState("SA");
  const [country, setCountry] = useState("IN");
  const [countriesList, setCountriesList] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [entityType, setEntityType] = useState("proprietorship");
  const [businessLocation, setBusinessLocation] = useState("district");

  const [yearsInBusiness, setYearsInBusiness] = useState(3);
  const [promoterExp, setPromoterExp] = useState(5);
  const [numEmployees, setNumEmployees] = useState(5);

  const [annualTurnover, setAnnualTurnover] = useState(5);
  const [netProfitMargin, setNetProfitMargin] = useState(5);
  const [bankLimits, setBankLimits] = useState(0);
  const [currentRatio, setCurrentRatio] = useState(1.5);
  const [debtEquity, setDebtEquity] = useState(1);
  const [interestCoverage, setInterestCoverage] = useState(1.5);

  const [buyerConcentration, setBuyerConcentration] = useState("medium");
  const [warehouseType, setWarehouseType] = useState("leased");
  const [logisticsType, setLogisticsType] = useState("third-party");
  const [inventoryHold, setInventoryHold] = useState(30);
  const [paymentTerms, setPaymentTerms] = useState(30);
  const [hedging, setHedging] = useState("no");

  const [documents, setDocuments] = useState({
    gst: false,
    itr: false,
    bank: false,
    fssai: false,
    cibil: false,
    udyam: false,
    audit: false,
    mandi: false,
    warehouse: false,
    iec: false,
  });

  const [finalScore, setFinalScore] = useState(0);
  const [grade, setGrade] = useState("");
  const [gradeColor, setGradeColor] = useState("#ef4444");

  // Breakdown state
  const [breakdown, setBreakdown] = useState({
    profile: { total: 0, vintage: 0, entity: 0, promoter: 0, vintageVal: "", entityVal: "" },
    financials: { total: 0, turnover: 0, profit: 0, ratio: 0, debt: 0, turnoverVal: "", profitVal: "" },
    compliance: { total: 0, docs: 0, reg: 0, docsCount: 0, regStatus: "" },
    operations: { total: 0, buyer: 0, asset: 0, cycle: 0, hedge: 0, buyerVal: "", assetVal: "", cycleVal: "" },
  });

  // Full region-country mapping
  const regionMapping = {
    SA: [{ code: "IN", name: "India (₹)" }],
    ME: [
      { code: "AE", name: "UAE (AED)" },
      { code: "SA", name: "Saudi Arabia (SAR)" },
      { code: "QA", name: "Qatar (QAR)" },
      { code: "KW", name: "Kuwait (KWD)" },
      { code: "OM", name: "Oman (OMR)" },
      { code: "BH", name: "Bahrain (BHD)" },
      { code: "JO", name: "Jordan (JOD)" },
      { code: "TR", name: "Türkiye (TRY)" },
      { code: "IL", name: "Israel (ILS)" },
    ],
    AF: [
      { code: "DZ", name: "Algeria (DZD)" },
      { code: "AO", name: "Angola (AOA)" },
      { code: "BJ", name: "Benin (XOF)" },
      { code: "BW", name: "Botswana (BWP)" },
      { code: "BF", name: "Burkina Faso (XOF)" },
      { code: "BI", name: "Burundi (BIF)" },
      { code: "CV", name: "Cabo Verde (CVE)" },
      { code: "CM", name: "Cameroon (XAF)" },
      { code: "CF", name: "Central African Republic (XAF)" },
      { code: "TD", name: "Chad (XAF)" },
      { code: "KM", name: "Comoros (KMF)" },
      { code: "CG", name: "Republic of the Congo (XAF)" },
      { code: "CD", name: "DR Congo (CDF)" },
      { code: "CI", name: "Côte d’Ivoire (XOF)" },
      { code: "DJ", name: "Djibouti (DJF)" },
      { code: "EG", name: "Egypt (EGP)" },
      { code: "GQ", name: "Equatorial Guinea (XAF)" },
      { code: "ER", name: "Eritrea (ERN)" },
      { code: "SZ", name: "Eswatini (SZL)" },
      { code: "ET", name: "Ethiopia (ETB)" },
      { code: "GA", name: "Gabon (XAF)" },
      { code: "GM", name: "Gambia (GMD)" },
      { code: "GH", name: "Ghana (GHS)" },
      { code: "GN", name: "Guinea (GNF)" },
      { code: "GW", name: "Guinea-Bissau (XOF)" },
      { code: "KE", name: "Kenya (KES)" },
      { code: "LS", name: "Lesotho (LSL)" },
      { code: "LR", name: "Liberia (LRD)" },
      { code: "LY", name: "Libya (LYD)" },
      { code: "MG", name: "Madagascar (MGA)" },
      { code: "MW", name: "Malawi (MWK)" },
      { code: "ML", name: "Mali (XOF)" },
      { code: "MR", name: "Mauritania (MRU)" },
      { code: "MU", name: "Mauritius (MUR)" },
      { code: "MA", name: "Morocco (MAD)" },
      { code: "MZ", name: "Mozambique (MZN)" },
      { code: "NA", name: "Namibia (NAD)" },
      { code: "NE", name: "Niger (XOF)" },
      { code: "NG", name: "Nigeria (NGN)" },
      { code: "RW", name: "Rwanda (RWF)" },
      { code: "ST", name: "São Tomé and Príncipe (STN)" },
      { code: "SN", name: "Senegal (XOF)" },
      { code: "SC", name: "Seychelles (SCR)" },
      { code: "SL", name: "Sierra Leone (SLE)" },
      { code: "SO", name: "Somalia (SOS)" },
      { code: "ZA", name: "South Africa (ZAR)" },
      { code: "SS", name: "South Sudan (SSP)" },
      { code: "SD", name: "Sudan (SDG)" },
      { code: "TZ", name: "Tanzania (TZS)" },
      { code: "TG", name: "Togo (XOF)" },
      { code: "TN", name: "Tunisia (TND)" },
      { code: "UG", name: "Uganda (UGX)" },
      { code: "ZM", name: "Zambia (ZMW)" },
      { code: "ZW", name: "Zimbabwe (ZWG)" },
    ],
  };

  useEffect(() => {
    const list = regionMapping[region] || [];
    setCountriesList(list);
    if (list.length > 0) {
      setCountry(list[0].code);
    }
  }, [region]);

  const countryData = {
    IN: {
      curr: "₹",
      unit: "Cr",
      entities: { prop: "Proprietorship", llp: "LLP", pvt: "Pvt Ltd", part: "Partnership" },
      docs: {
        tax: "GST Registration Certificate",
        ret: "ITR Filed (Last 2 Years)",
        sme: "UDYAM Aadhar (MSME)",
        trade: "Mandi / APMC License",
        credit: "CIBIL/Credit Report",
      },
    },
    AE: {
      curr: "AED",
      unit: "M",
      entities: { prop: "Sole Establishment", llp: "Civil Company", pvt: "LLC (Limited Liability)", part: "Branch of Foreign Co" },
      docs: {
        tax: "VAT Registration",
        ret: "Audited Financials / Returns",
        sme: "SME License (Dubai SME)",
        trade: "General Trading License",
        credit: "Etihad Credit Bureau Report",
      },
    },
    // Add more countries as needed
  };

  const config = countryData[country] || countryData["IN"];

  const resetForm = () => {
    setCurrentStep(1);
    setShowResult(false);
    setRegion("SA");
    setCountry("IN");
    setCompanyName("");
    setEntityType("proprietorship");
    setBusinessLocation("district");
    setYearsInBusiness(3);
    setPromoterExp(5);
    setNumEmployees(5);
    setAnnualTurnover(5);
    setNetProfitMargin(5);
    setBankLimits(0);
    setCurrentRatio(1.5);
    setDebtEquity(1);
    setInterestCoverage(1.5);
    setBuyerConcentration("medium");
    setWarehouseType("leased");
    setLogisticsType("third-party");
    setInventoryHold(30);
    setPaymentTerms(30);
    setHedging("no");
    setDocuments({
      gst: false,
      itr: false,
      bank: false,
      fssai: false,
      cibil: false,
      udyam: false,
      audit: false,
      mandi: false,
      warehouse: false,
      iec: false,
    });
    setFinalScore(0);
    setGrade("");
    setGradeColor("#ef4444");
    setBreakdown({
      profile: { total: 0, vintage: 0, entity: 0, promoter: 0, vintageVal: "", entityVal: "" },
      financials: { total: 0, turnover: 0, profit: 0, ratio: 0, debt: 0, turnoverVal: "", profitVal: "" },
      compliance: { total: 0, docs: 0, reg: 0, docsCount: 0, regStatus: "" },
      operations: { total: 0, buyer: 0, asset: 0, cycle: 0, hedge: 0, buyerVal: "", assetVal: "", cycleVal: "" },
    });
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateScore();
      setShowResult(true);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const calculateScore = () => {
    let score = 0;
    const bd = {
      profile: { total: 0, vintage: 0, entity: 0, promoter: 0, vintageVal: `${yearsInBusiness} Years`, entityVal: config.entities[entityType === "pvt-ltd" ? "pvt" : entityType === "proprietorship" ? "prop" : entityType] || entityType },
      financials: { total: 0, turnover: 0, profit: 0, ratio: 0, debt: 0, turnoverVal: `${config.curr} ${annualTurnover} ${config.unit}`, profitVal: `${netProfitMargin}%` },
      compliance: { total: 0, docs: 0, reg: 0, docsCount: 0, regStatus: "Basic" },
      operations: { total: 0, buyer: 0, asset: 0, cycle: 0, hedge: 0, buyerVal: buyerConcentration.charAt(0).toUpperCase() + buyerConcentration.slice(1), assetVal: "Leased", cycleVal: `${Math.round((inventoryHold + paymentTerms) / 2)} Avg Days` },
    };

    // Profile
    bd.profile.vintage = Math.min(Math.round(yearsInBusiness * 1.33), 10);
    bd.profile.entity = entityType === "pvt-ltd" ? 5 : entityType === "llp" ? 4 : entityType === "partnership" ? 2 : 0;
    bd.profile.promoter = Math.min(promoterExp, 5);
    bd.profile.total = bd.profile.vintage + bd.profile.entity + bd.profile.promoter;
    score += bd.profile.total;

    // Financials
    bd.financials.turnover = Math.min(Math.round(annualTurnover * 0.8), 10);
    bd.financials.profit = Math.min(Math.round(netProfitMargin), 8);
    bd.financials.ratio = currentRatio >= 1.2 ? 5 : currentRatio >= 1.0 ? 3 : 0;
    bd.financials.debt = debtEquity < 0.5 ? 5 : debtEquity <= 1.0 ? 4 : debtEquity <= 1.5 ? 2 : 0;
    bd.financials.total = bd.financials.turnover + bd.financials.profit + bd.financials.ratio + bd.financials.debt;
    score += bd.financials.total;

    // Compliance
    bd.compliance.docsCount = Object.values(documents).filter(Boolean).length;
    bd.compliance.docs = Math.min(bd.compliance.docsCount * 2, 20);
    if (documents.audit) {
      bd.compliance.reg = 5;
      bd.compliance.regStatus = "Audited";
    } else if (documents.itr) {
      bd.compliance.reg = 3;
      bd.compliance.regStatus = "Declared";
    }
    bd.compliance.total = bd.compliance.docs + bd.compliance.reg;
    score += bd.compliance.total;

    // Operations
    bd.operations.buyer = buyerConcentration === "low" ? 5 : buyerConcentration === "medium" ? 3 : 0;
    let assetPts = 0;
    if (warehouseType === "owned") assetPts += 3;
    else if (warehouseType === "leased") assetPts += 1;
    if (logisticsType === "owned") assetPts += 2;
    bd.operations.asset = assetPts;
    bd.operations.assetVal = assetPts >= 4 ? "Strong Asset Base" : assetPts >= 2 ? "Mixed Assets" : "Leased";

    bd.operations.cycle = (inventoryHold <= 30 ? 5 : inventoryHold <= 60 ? 3 : 0) + (paymentTerms <= 30 ? 5 : paymentTerms <= 60 ? 3 : 0);
    bd.operations.hedge = hedging === "yes" ? 5 : 0;
    bd.operations.total = bd.operations.buyer + bd.operations.asset + bd.operations.cycle + bd.operations.hedge;
    score += bd.operations.total;

    score = Math.min(100, Math.max(0, Math.round(score)));
    setFinalScore(score);
    setBreakdown(bd);

    let g = "D (Critical Risk)";
    let color = "#ef4444";
    if (score >= 75) { g = "A (Excellent)"; color = "#51842f"; }
    else if (score >= 60) { g = "B (Good)"; color = "#10b981"; }
    else if (score >= 40) { g = "C (Moderate Risk)"; color = "#f59e0b"; }
    setGrade(g);
    setGradeColor(color);
  };

  return (
    <Box sx={{ py: 4, px: 2 }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600 }}>
        Self Assessment Credit Score Checker
      </Typography>
      <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
        Evaluate your creditworthiness to make smarter trade and financing decisions
      </Typography>

      <CalculatorCard elevation={0}>
        <WizardNav>
          {["Profile", "Financials", "Documents", "Operations"].map((label, i) => (
            <NavStep key={i} active={currentStep === i + 1 || (showResult && i < 4)}>
              {label}
            </NavStep>
          ))}
        </WizardNav>

        <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "500px" }}>
          {!showResult ? (
            <>
              {/* Step 1: Profile */}
              {currentStep === 1 && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem" }}>
                  <Box>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Select Region</InputLabel>
                      <Select value={region} onChange={(e) => setRegion(e.target.value)}>
                        <MenuItem value="SA">South Asia</MenuItem>
                        <MenuItem value="ME">Middle East</MenuItem>
                        <MenuItem value="AF">Africa</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Select Country</InputLabel>
                      <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                        {countriesList.map((c) => (
                          <MenuItem key={c.code} value={c.code}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Company Name"
                      placeholder="Enter business name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      sx={{ mb: 3 }}
                    />
                    <InfoTip>Identify your business. While this doesn't directly affect score, it's used for report generation.</InfoTip>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 3 }}>Legal Entity Type</Typography>
                    <Box>
                      {["proprietorship", "llp", "pvt-ltd", "partnership"].map((val) => {
                        const label = val === "proprietorship" ? config.entities.prop :
                                      val === "llp" ? config.entities.llp :
                                      val === "pvt-ltd" ? config.entities.pvt :
                                      config.entities.part;
                        return (
                          <RadioCard key={val} checked={entityType === val} onClick={() => setEntityType(val)}>
                            <Box className="card-content">
                              <Box className="radio-circle" />
                              <Typography>{label}</Typography>
                            </Box>
                          </RadioCard>
                        );
                      })}
                    </Box>
                    <InfoTip>Corporates (Pvt Ltd) and LLPs score higher as they have better regulatory compliance.</InfoTip>
                  </Box>

                  <Box>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Business Location</InputLabel>
                      <Select value={businessLocation} onChange={(e) => setBusinessLocation(e.target.value)}>
                        <MenuItem value="district">Within District</MenuItem>
                        <MenuItem value="state">Inter-State</MenuItem>
                        <MenuItem value="national">National</MenuItem>
                        <MenuItem value="export">Export</MenuItem>
                      </Select>
                      <InfoTip>Broader geographic operations suggest a more scalable business model.</InfoTip>
                    </FormControl>

                    <Box sx={{ mb: 3 }}>
                      <LabelRow>
                        <Typography>Years in Business</Typography>
                        <RangeValue>{yearsInBusiness} Years</RangeValue>
                      </LabelRow>
                      <Slider value={yearsInBusiness} onChange={(_, v) => setYearsInBusiness(v)} min={0} max={25} />
                      <InfoTip>Vintage matters. &gt;5 years indicates stability.</InfoTip>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <LabelRow>
                        <Typography>Promoter Experience (Optional)</Typography>
                        <RangeValue>{promoterExp} Years</RangeValue>
                      </LabelRow>
                      <Slider value={promoterExp} onChange={(_, v) => setPromoterExp(v)} min={0} max={40} />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <LabelRow>
                        <Typography>Number of Employees (Optional)</Typography>
                        <RangeValue>{numEmployees}+</RangeValue>
                      </LabelRow>
                      <Slider value={numEmployees} onChange={(_, v) => setNumEmployees(v)} min={1} max={100} />
                      <InfoTip>Larger teams indicate operational scale.</InfoTip>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Step 2: Financials */}
              {currentStep === 2 && (
                <Box>
                  <Box sx={{ bgcolor: "#eff6ff", p: 2, borderRadius: 1, mb: 4, border: "1px solid #bfdbfe", display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4m0-4h.01" strokeLinecap="round" />
                    </svg>
                    <Typography>Use the sliders to indicate your approximate financial standing. Exact figures are not required for this preliminary assessment.</Typography>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <LabelRow>
                      <Typography>Annual Turnover (Last FY)</Typography>
                      <RangeValue>{config.curr} {annualTurnover} {config.unit}</RangeValue>
                    </LabelRow>
                    <Slider value={annualTurnover} onChange={(_, v) => setAnnualTurnover(v)} min={0} max={50} step={0.5} />
                    <InfoTip>Revenue scale is a key indicator of credit capacity. Higher turnover (&gt; {config.curr}10 {config.unit}) enables larger credit limits.</InfoTip>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem" }}>
                    <Box>
                      <LabelRow>
                        <Typography>Net Profit Margin</Typography>
                        <RangeValue>{netProfitMargin} %</RangeValue>
                      </LabelRow>
                      <Slider value={netProfitMargin} onChange={(_, v) => setNetProfitMargin(v)} min={0} max={20} step={0.5} />
                      <InfoTip>Higher margins (&gt;3%) provide a financial cushion against market volatility.</InfoTip>
                    </Box>

                    <Box>
                      <LabelRow>
                        <Typography>Existing Bank Limits</Typography>
                        <RangeValue>{["None", "Low", "Medium", "High", "Very High"][bankLimits]}</RangeValue>
                      </LabelRow>
                      <Slider value={bankLimits} onChange={(_, v) => setBankLimits(v)} min={0} max={4} step={1} />
                      <InfoTip>Existing bank relationships validate your creditworthiness. Higher existing limits score positively.</InfoTip>
                    </Box>

                    <Box>
                      <LabelRow>
                        <Typography>Current Ratio</Typography>
                        <RangeValue>{currentRatio} : 1</RangeValue>
                      </LabelRow>
                      <Slider value={currentRatio} onChange={(_, v) => setCurrentRatio(v)} min={0.5} max={3} step={0.1} />
                      <InfoTip>Measures liquidity (Assets/Liabilities). A ratio &gt; 1.2 ensures you can meet short-term obligations.</InfoTip>
                    </Box>

                    <Box>
                      <LabelRow>
                        <Typography>Debt-to-Equity Ratio</Typography>
                        <RangeValue>{debtEquity} : 1</RangeValue>
                      </LabelRow>
                      <Slider value={debtEquity} onChange={(_, v) => setDebtEquity(v)} min={0} max={5} step={0.1} />
                      <InfoTip>Measures leverage. Lower is better. A ratio &lt; 2 indicates a healthy balance sheet not overly reliant on debt.</InfoTip>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <LabelRow>
                      <Typography>Interest Coverage Ratio (Optional)</Typography>
                      <RangeValue>{interestCoverage}</RangeValue>
                    </LabelRow>
                    <Slider value={interestCoverage} onChange={(_, v) => setInterestCoverage(v)} min={0} max={10} step={0.1} />
                    <InfoTip>(EBIT / Interest Expense). A ratio &gt; 1.5 shows you generate enough earnings to comfortably pay interest on loans.</InfoTip>
                  </Box>
                </Box>
              )}

              {/* Step 3: Documents */}
              {currentStep === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom className="section-heading">Compliance Checklist</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom className="section-sub">
                    Select the documents currently available with you.
                  </Typography>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", mt: 3 }}>
                    <Box>
                      {["gst", "itr", "bank", "fssai", "cibil"].map((key) => (
                        <DocCard key={key} checked={documents[key]} onClick={() => setDocuments((prev) => ({ ...prev, [key]: !prev[key] }))}>
                          <Box className="doc-box" />
                          <Box>
                            <Typography className="doc-title">
                              {key === "gst" ? config.docs.tax : key === "itr" ? config.docs.ret : key === "cibil" ? config.docs.credit : key === "bank" ? "Bank Statements (6 months)" : "FSSAI / Food Safety License"}
                            </Typography>
                            <Typography className="doc-desc" sx={{ color: "#9ca3af" }}>
                              {key === "gst" ? "Mandatory for verifying legal existence." : key === "itr" ? "Verifies declared income stability." : key === "bank" ? "Used to analyze cash flow patterns." : key === "fssai" ? "Necessary for food safety compliance." : "Bonus: Shows proactive credit history management."}
                            </Typography>
                          </Box>
                        </DocCard>
                      ))}
                    </Box>

                    <Box>
                      {["udyam", "audit", "mandi", "warehouse", "iec"].map((key) => (
                        <DocCard key={key} checked={documents[key]} onClick={() => setDocuments((prev) => ({ ...prev, [key]: !prev[key] }))}>
                          <Box className="doc-box" />
                          <Box>
                            <Typography className="doc-title">
                              {key === "udyam" ? config.docs.sme : key === "audit" ? "Audited Financial Statements" : key === "mandi" ? config.docs.trade : key === "warehouse" ? "Warehouse Insurance" : "IEC Code (Import/Export)"}
                            </Typography>
                            <Typography className="doc-desc" sx={{ color: "#9ca3af" }}>
                              {key === "udyam" ? "Proof of SME status, aids in priority lending." : key === "audit" ? "Crucial for high trust score & higher limits." : key === "mandi" ? "Required for agricultural trading legality." : key === "warehouse" ? "Mitigates risk of stock loss (fire/theft)." : "Bonus: Required if you plan to trade internationally."}
                            </Typography>
                          </Box>
                        </DocCard>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Step 4: Operations */}
              {currentStep === 4 && (
                <Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem" }}>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>Top 5 Buyer Concentration</Typography>
                      <SegmentControl sx={{ mt: 1 }}>
                        {["low", "medium", "high"].map((val) => (
                          <SegmentOption key={val} selected={buyerConcentration === val} onClick={() => setBuyerConcentration(val)}>
                            {val.charAt(0).toUpperCase() + val.slice(1)}
                          </SegmentOption>
                        ))}
                      </SegmentControl>
                      <InfoTip>High concentration means relying on too few buyers. 'Low' is better as it spreads risk.</InfoTip>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" gutterBottom>Warehousing Type</Typography>
                      <SegmentControl sx={{ mt: 1 }}>
                        {["owned", "leased", "third-party"].map((val) => (
                          <SegmentOption key={val} selected={warehouseType === val} onClick={() => setWarehouseType(val)}>
                            {val.charAt(0).toUpperCase() + val.slice(1).replace("-", " ")}
                          </SegmentOption>
                        ))}
                      </SegmentControl>
                      <InfoTip>Owning warehouses implies asset strength and better control over stock.</InfoTip>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" gutterBottom>Logistics / Transport</Typography>
                      <SegmentControl sx={{ mt: 1 }}>
                        {["owned", "third-party"].map((val) => (
                          <SegmentOption key={val} selected={logisticsType === val} onClick={() => setLogisticsType(val)}>
                            {val.charAt(0).toUpperCase() + val.slice(1).replace("-", " ")}
                          </SegmentOption>
                        ))}
                      </SegmentControl>
                      <InfoTip>Owning your fleet suggests operational robustness.</InfoTip>
                    </Box>
                  </Box>

                  <Box sx={{ mt: "1.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem" }}>
                    <Box>
                      <LabelRow>
                        <Typography>Inventory Holding Period</Typography>
                        <RangeValue>{inventoryHold} Days</RangeValue>
                      </LabelRow>
                      <Slider value={inventoryHold} onChange={(_, v) => setInventoryHold(v)} min={0} max={120} />
                      <InfoTip>Faster stock turnover (lower days) is better.</InfoTip>
                    </Box>

                    <Box>
                      <LabelRow>
                        <Typography>Payment Terms (Receivables)</Typography>
                        <RangeValue>{paymentTerms} Days</RangeValue>
                      </LabelRow>
                      <Slider value={paymentTerms} onChange={(_, v) => setPaymentTerms(v)} min={0} max={90} />
                      <InfoTip>How fast buyers pay you. Shorter cycles (&lt;45 days) improve cash flow.</InfoTip>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: "#f9fafb", borderRadius: 1, border: "1px solid #e5e7eb" }}>
                        <Box>
                          <Typography variant="subtitle1">Hedging Mechanism</Typography>
                          <Typography variant="body2" color="text.secondary">Do you use futures/options to hedge price risk?</Typography>
                        </Box>
                        <ToggleSwitch>
                          <ToggleOption selected={hedging === "yes"} onClick={() => setHedging("yes")}>Yes</ToggleOption>
                          <ToggleOption selected={hedging === "no"} onClick={() => setHedging("no")}>No</ToggleOption>
                        </ToggleSwitch>
                      </Box>
                      <InfoTip>Hedging protects against commodity price fluctuations. Using it significantly lowers risk score.</InfoTip>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box>
              <ResultHeader>
                <Box>
                  <Typography variant="h5">Assessment Complete</Typography>
                  <Typography>Self-declared Information Analysis</Typography>
                </Box>
                <Box>
                  <PrimaryButton onClick={() => window.print()} sx={{ mr: 2 }}>
                    Print Report
                  </PrimaryButton>
                  <PrimaryButton onClick={resetForm}>
                    Start New
                  </PrimaryButton>
                </Box>
              </ResultHeader>

              <Box sx={{ display: "grid", gridTemplateColumns: { md: "350px 1fr" }, gap: "2rem", px: 2, mt: "15px" }}>
                <ScoreCardMain elevation={0}>
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <CircularProgress variant="determinate" value={finalScore} size={200} thickness={5} sx={{ color: gradeColor }} />
                    <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                      <Typography variant="h3" fontWeight="bold">{finalScore}</Typography>
                      <Typography variant="body2" color="text.secondary">OUT OF 100</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h5" sx={{ color: gradeColor }}>{grade}</Typography>
                    <Typography variant="body1" color="text.secondary">Estimated Credit Standing</Typography>
                  </Box>
                  <Box sx={{ mt: 3, bgcolor: "#eff6ff", p: 2, borderRadius: 2, border: "1px solid #dbeafe" }}>
                    <Typography variant="body2" color="#1e40af">
                      This score is indicative based on self-reported data. A physical audit and bank statement verification is required for final credit limit approval.
                    </Typography>
                  </Box>
                </ScoreCardMain>

                <Box>
                  <Typography variant="h6" gutterBottom>Score Breakdown</Typography>
                  <Box sx={{ maxHeight: "600px", overflowY: "auto" }}>
                    {/* PROFILE */}
                    <BdSection>
                      <BdHeader>
                        <span>PROFILE</span>
                        <BdBadge>{breakdown.profile.total} Points</BdBadge>
                      </BdHeader>
                      <BdItem>
                        <BdLabel>
                          <span>Business Vintage</span>
                          <small>{breakdown.profile.vintageVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.profile.vintage}</span>
                          <small>max 10</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Legal Entity</span>
                          <small>{breakdown.profile.entityVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.profile.entity}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Promoter Exp.</span>
                          <small>{promoterExp} Years</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.profile.promoter}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                    </BdSection>

                    {/* FINANCIALS */}
                    <BdSection>
                      <BdHeader>
                        <span>FINANCIALS</span>
                        <BdBadge>{breakdown.financials.total} Points</BdBadge>
                      </BdHeader>
                      <BdItem>
                        <BdLabel>
                          <span>Annual Turnover</span>
                          <small>{breakdown.financials.turnoverVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.financials.turnover}</span>
                          <small>max 10</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Profit Margin</span>
                          <small>{breakdown.financials.profitVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.financials.profit}</span>
                          <small>max 8</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Current Ratio</span>
                          <small>{currentRatio}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.financials.ratio}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Debt-to-Equity</span>
                          <small>{debtEquity}:1</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.financials.debt}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                    </BdSection>

                    {/* COMPLIANCE */}
                    <BdSection>
                      <BdHeader>
                        <span>COMPLIANCE</span>
                        <BdBadge>{breakdown.compliance.total} Points</BdBadge>
                      </BdHeader>
                      <BdItem>
                        <BdLabel>
                          <span>Documents Available</span>
                          <small>{breakdown.compliance.docsCount} / 10</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.compliance.docs}</span>
                          <small>max 20</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Regulatory Status</span>
                          <small>{breakdown.compliance.regStatus}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.compliance.reg}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                    </BdSection>

                    {/* OPERATIONS */}
                    <BdSection>
                      <BdHeader>
                        <span>OPERATIONS</span>
                        <BdBadge>{breakdown.operations.total} Points</BdBadge>
                      </BdHeader>
                      <BdItem>
                        <BdLabel>
                          <span>Buyer Risk</span>
                          <small>{breakdown.operations.buyerVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.operations.buyer}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Asset Control</span>
                          <small>{breakdown.operations.assetVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.operations.asset}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Cycle Efficiency</span>
                          <small>{breakdown.operations.cycleVal}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.operations.cycle}</span>
                          <small>max 10</small>
                        </BdPoints>
                      </BdItem>
                      <BdItem>
                        <BdLabel>
                          <span>Hedging</span>
                          <small>{hedging === "yes" ? "Yes" : "No"}</small>
                        </BdLabel>
                        <BdPoints>
                          <span className="plus">+{breakdown.operations.hedge}</span>
                          <small>max 5</small>
                        </BdPoints>
                      </BdItem>
                    </BdSection>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {!showResult && (
          <FooterActions>
            <Button variant="outlined" onClick={handlePrev} disabled={currentStep === 1}>
              Back
            </Button>
            <PrimaryButton onClick={handleNext}>
              {currentStep === totalSteps ? "Calculate Score" : "Next Step"}
            </PrimaryButton>
          </FooterActions>
        )}
      </CalculatorCard>
    </Box>
  );
};

export default AgriTradeCreditAssess;